/**
 * Shared utility for reading CSRF token from document.cookie.
 *
 * This follows the double-submit cookie pattern where the token is read from
 * a cookie and must be sent back in a custom header (e.g., X-CSRF-Token).
 */

/**
 * Interface for optional logging support
 */
export interface CSRFLogger {
  warn: (message: string, metadata?: any) => void;
  debug: (message: string, metadata?: any) => void;
  error: (message: string, metadata?: any) => void;
}

/**
 * Attempts to decode a URL-encoded cookie value.
 * @param value The encoded cookie value
 * @param logger Optional logger for error reporting
 * @returns The decoded value, or null if decoding fails
 */
const parseCookieValue = (value: string, logger?: CSRFLogger): string | null => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    if (logger) {
      logger.error('Failed to decode CSRF token cookie value', e);
    }
    return null;
  }
};

/**
 * Extracts all CSRF tokens from the cookie string.
 * @param cookies Array of cookie strings
 * @param logger Optional logger for debugging
 * @returns Array of decoded CSRF token values
 */
const extractCsrfTokens = (cookies: string[], logger?: CSRFLogger): string[] => {
  const tokens: string[] = [];

  for (const cookie of cookies) {
    const parts = cookie.trim().split('=');
    const name = parts[0];
    const value = parts.slice(1).join('='); // Join remaining parts to support values with '='

    if (name === 'csrf_token') {
      const decodedValue = parseCookieValue(value, logger);
      if (decodedValue !== null) {
        tokens.push(decodedValue);
      }
    }
  }

  return tokens;
};

/**
 * Reads the CSRF token from document.cookie.
 *
 * Mirroring server-side behavior, if multiple tokens are found,
 * it returns the last one found.
 *
 * @param logger Optional logger for debugging and warnings
 * @returns The CSRF token if found, or null otherwise
 */
export const getCsrfTokenFromDocumentCookie = (logger?: CSRFLogger): string | null => {
  if (typeof document === 'undefined' || !document.cookie) {
    if (logger) {
      logger.debug(
        'document.cookie is not available. This is expected in a non-browser environment.',
      );
    }
    return null;
  }

  try {
    const cookies = document.cookie.split(';');
    const tokens = extractCsrfTokens(cookies, logger);

    if (tokens.length > 1 && logger) {
      logger.warn('Found multiple CSRF tokens in cookies. This indicates stale cookies.', {
        count: tokens.length,
      });
    }

    if (tokens.length > 0) {
      // Return the last CSRF token found (standard server-side parsing behavior)
      return tokens[tokens.length - 1];
    }

    if (logger) {
      logger.debug('CSRF token cookie not found in document.cookie');
    }
  } catch (e) {
    if (logger) {
      logger.error('Error reading CSRF cookie from document.cookie', e);
    }
  }

  return null;
};
